import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'

export default function BalanceManagementModal({ 
  isOpen, 
  onClose, 
  user, 
  onBalanceUpdated 
}) {
  const [operation, setOperation] = useState('add')
  const [currency, setCurrency] = useState('krw')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const formatBalance = (balance) => {
    if (typeof balance === 'string') {
      // For very large numbers returned as strings
      return parseFloat(balance).toLocaleString()
    }
    return Number(balance || 0).toLocaleString()
  }

  useEffect(() => {
    if (isOpen) {
      setOperation('add')
      setCurrency('krw')
      setAmount('')
      setDescription('')
      setError('')
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('올바른 금액을 입력해주세요.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const amountValue = parseFloat(amount);
      // For very large numbers, send as string to avoid precision loss
      const finalAmount = amountValue > Number.MAX_SAFE_INTEGER ? amount : amountValue;
      
      await api.put('/users/balance', {
        userId: user.id,
        currency,
        amount: finalAmount,
        operation,
        description: description || `${operation === 'add' ? '지급' : '차감'}: ${currency === 'krw' ? '원화' : '토큰'} ${amount}`
      })

      // Trigger refresh of user data
      if (onBalanceUpdated) {
        onBalanceUpdated()
      }

      onClose()
    } catch (error) {
      console.error('Failed to update balance:', error)
      setError(error.response?.data?.message || '잔액 업데이트에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            잔액 관리 - {user.nickname || user.username}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">현재 잔액</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>원화:</span>
              <span className="font-medium">{formatBalance(user.krwBalance)}원</span>
            </div>
            <div className="flex justify-between">
              <span>토큰:</span>
              <span className="font-medium">{formatBalance(user.tokenBalance)}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              작업 유형
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="operation"
                  value="add"
                  checked={operation === 'add'}
                  onChange={(e) => setOperation(e.target.value)}
                  className="mr-2"
                />
                지급
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="operation"
                  value="subtract"
                  checked={operation === 'subtract'}
                  onChange={(e) => setOperation(e.target.value)}
                  className="mr-2"
                />
                차감
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              통화
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="krw"
                  checked={currency === 'krw'}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="mr-2"
                />
                원화
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="currency"
                  value="token"
                  checked={currency === 'token'}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="mr-2"
                />
                토큰
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              금액
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="any"
              placeholder="금액을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              설명 (선택사항)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="거래 설명을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 text-white rounded-md transition-colors ${
                operation === 'add'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? '처리중...' : operation === 'add' ? '지급하기' : '차감하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}